// Cross-class league tables (C-7): networks, issuers, platforms and asset
// classes, summed over every class board and stock venue, and a feed of the
// products most recently added to tracking. Pure: the component loads the
// boards and passes them in, so every rule is tested here.
//
// Counting each product once. The class boards and the stock venues never
// list the same token, so their sums never overlap. Stablecoins overlap the
// classes (a fund listed as a stablecoin too, or held in a stablecoin's
// reserve); /v1/classes lists those, and they matter only when stablecoins
// are included. A fund listed as a stablecoin is taken off the stablecoin
// side chain by chain, from that coin's own chain split; a reserve holding
// comes off the chain `by_chain` names, and is reported apart when it has none.

import { chainSplit, MULTI_CHAIN, type VenueLoad } from "./all-venues";
import { chainLabel } from "./api";
import { cleanFlow } from "./flows";
import { compactUsd } from "./format";
import { ASSET_CLASSES, classHref, type ClassBoard, type ClassIndex } from "./classes";
import type { Stablecoins } from "./stablecoins";

export type LeagueTab = "networks" | "issuers" | "platforms" | "classes";

export const LEAGUE_TABS: ReadonlyArray<readonly [LeagueTab, string]> = [
  ["networks", "Networks"],
  ["issuers", "Issuers & managers"],
  ["platforms", "Platforms"],
  ["classes", "Asset classes"],
];

/** The bucket for value no chain can be given: protocol TVL, and stock tokens whose chain supplies double count. */
export const UNSPLIT = "unsplit";

export interface LeagueRow {
  key: string;
  name: string;
  /** Products counted in the row; null when not known. */
  products: number | null;
  valueUsd: number;
  /** Of the headline the table is measured against. */
  share: number | null;
  /** 7-day change, fraction; null when the API has none for this row. */
  change7d: number | null;
  /** 7-day net flow, USD; null when not measured (asset classes only). */
  flow7d?: number | null;
  /** Where the row's products come from: "Treasuries, Credit", "Stocks". */
  detail: string | null;
  /** A page to open for the row. */
  href: string | null;
  /** Sorted last and unranked: not a real network (the unsplit bucket). */
  unranked?: boolean;
}

export interface LeagueInput {
  index: ClassIndex;
  /** Loaded registry class boards. */
  boards: readonly ClassBoard[];
  venues: readonly VenueLoad[];
  /** Null while not loaded, or when stablecoins are left out. */
  stablecoins: Stablecoins | null;
}

export interface League {
  rows: LeagueRow[];
  /** The headline the shares are taken of. */
  headline: number | null;
  /** Value the table cannot place in a row, said in words; null when none. */
  notes: string[];
}

const finite = (value: number | null | undefined): value is number => value !== null && value !== undefined && Number.isFinite(value);

const classInfo = (apiId: string) => ASSET_CLASSES.find((c) => c.apiId === apiId);

/**
 * The headline the shares are measured against: /v1/classes' total, each
 * product once, with stablecoins; without them, the other classes summed
 * (the overlap is all on the stablecoin side, so it falls away with them).
 */
export function leagueHeadline(index: Pick<ClassIndex, "total_value_usd" | "classes">, includeStablecoins: boolean): number | null {
  if (includeStablecoins) return index.total_value_usd;
  const values = index.classes.filter((c) => c.id !== "stablecoin").map((c) => c.totals.value_usd);
  return values.some(finite) ? values.filter(finite).reduce((sum, v) => sum + v, 0) : null;
}

/**
 * The input narrowed to one class (a /v1/classes id): its board, the stock
 * venues or the stablecoins alone, with shares of that class's total. A class
 * on its own overlaps nothing, so nothing is taken off.
 */
export function onlyClass(input: LeagueInput, classId: string): LeagueInput {
  const item = input.index.classes.find((c) => c.id === classId);
  return {
    index: { ...input.index, total_value_usd: item?.totals.value_usd ?? null, overlap: [], overlap_value_usd: 0, classes: item ? [item] : [] },
    boards: input.boards.filter((b) => b.asset_class === classId),
    venues: classId === "stock" ? input.venues : [],
    stablecoins: classId === "stablecoin" ? input.stablecoins : null,
  };
}

function finish(rows: Map<string, LeagueRow & { sources: Set<string> }>, headline: number | null): LeagueRow[] {
  return [...rows.values()]
    .filter((row) => row.valueUsd > 0 || row.products)
    .map(({ sources, ...row }) => ({
      ...row,
      detail: row.detail ?? (sources.size ? [...sources].join(", ") : null),
      share: finite(headline) && headline > 0 ? row.valueUsd / headline : null,
    }))
    .sort((a, b) => Number(Boolean(a.unranked)) - Number(Boolean(b.unranked)) || b.valueUsd - a.valueUsd);
}

/** Each row's largest single contribution so far, for its name. */
const largestPart = new WeakMap<object, number>();

function bump(
  rows: Map<string, LeagueRow & { sources: Set<string> }>,
  key: string,
  name: string,
  value: number,
  products: number,
  source: string,
  extra: Partial<LeagueRow> = {},
): void {
  const row = rows.get(key) ?? { key, name, products: 0, valueUsd: 0, share: null, change7d: null, detail: null, href: null, sources: new Set<string>(), ...extra };
  // A row merged from several sources (Tether's gold and its stablecoins) takes the name of its largest part.
  const largest = largestPart.get(row) ?? -1;
  if (value > largest) {
    row.name = name;
    largestPart.set(row, value);
  }
  row.valueUsd += value;
  row.products = (row.products ?? 0) + products;
  row.sources.add(source);
  rows.set(key, row);
}

// ---- networks ----

/** DefiLlama's chain names onto the ids the boards use, for boards from before `chain_id` (C-7b). */
const LLAMA_CHAINS: Record<string, string> = {
  bsc: "bsc",
  "bnb chain": "bsc",
  xdai: "gnosis",
  "op mainnet": "optimism",
  "zksync era": "zksync",
  xrpl: "xrpl",
  "xrp ledger": "xrpl",
  "hyperliquid l1": "hyperevm",
  "avalanche c-chain": "avalanche",
};

/** A chain id from either source, so "Ethereum" (DefiLlama) and "ethereum" (boards) meet. */
export function chainKey(name: string): string {
  const lower = name.trim().toLowerCase();
  return LLAMA_CHAINS[lower] ?? lower.replace(/\s+/g, "");
}

/** A stablecoin chain's key: the API's `chain_id` where it sends one, else the name mapped. */
const stableChainKey = (c: { chain: string; chain_id?: string | null }): string => (c.chain_id ? c.chain_id : chainKey(c.chain));

export function networkLeague({ index, boards, venues, stablecoins }: LeagueInput): League {
  const headline = leagueHeadline(index, stablecoins !== null);
  const rows = new Map<string, LeagueRow & { sources: Set<string> }>();
  const unsplit = (value: number, source: string) => bump(rows, UNSPLIT, "Not split by chain", value, 0, source, { unranked: true });

  for (const board of boards) {
    const label = classInfo(board.asset_class)?.short ?? board.name;
    let onChains = 0;
    for (const c of board.chains) {
      if (!finite(c.value_usd)) continue;
      onChains += c.value_usd;
      bump(rows, chainKey(c.chain), chainLabel(c.chain), c.value_usd, c.entries, label);
    }
    // Protocol TVL is in the class total but on no chain.
    const rest = (board.totals.value_usd ?? 0) - onChains;
    if (rest > Math.max(1, (board.totals.value_usd ?? 0) * 0.001)) unsplit(rest, label);
  }

  for (const venue of venues) {
    if (!venue.board) continue;
    for (const part of chainSplit(venue.board.tokens)) {
      if (part.valueUsd <= 0) continue;
      if (part.chain === MULTI_CHAIN) unsplit(part.valueUsd, "Stocks");
      else bump(rows, chainKey(part.chain), chainLabel(part.chain), part.valueUsd, part.tokens, "Stocks");
    }
  }

  const notes: string[] = [];
  if (stablecoins) {
    const deducted = (index.overlap ?? []).filter((o) => o.deducted);
    // The stablecoin copy of a fund the classes already count comes off its chains.
    const sameToken = new Set(deducted.filter((o) => o.reason !== "backed_by").map((o) => o.stablecoin.id));
    const minus = new Map<string, { value: number; coins: number }>();
    for (const coin of stablecoins.stablecoins) {
      if (!sameToken.has(coin.id)) continue;
      for (const c of coin.chains ?? []) {
        if (!finite(c.market_cap) || c.market_cap <= 0) continue;
        const key = stableChainKey(c);
        const m = minus.get(key) ?? { value: 0, coins: 0 };
        m.value += c.market_cap;
        m.coins += 1;
        minus.set(key, m);
      }
    }
    for (const c of stablecoins.chains ?? []) {
      if (!finite(c.market_cap)) continue;
      const key = stableChainKey(c);
      const m = minus.get(key);
      const value = c.market_cap - (m?.value ?? 0);
      const products = (finite(c.stablecoins) ? c.stablecoins : 0) - (m?.coins ?? 0);
      if (value > 0) bump(rows, key, rows.get(key)?.name ?? (c.chain_id ? chainLabel(c.chain_id) : c.chain), value, Math.max(0, products), "Stablecoins");
    }
    // A reserve holding is counted in its class and in the stablecoin: taken off where the API says it sits.
    let unplaced = 0;
    for (const o of deducted.filter((x) => x.reason === "backed_by")) {
      const parts = (o.by_chain ?? []).filter((p) => finite(p.amount_usd) && p.amount_usd > 0);
      if (!parts.length) {
        unplaced += o.value_usd ?? 0;
        continue;
      }
      for (const p of parts) {
        const key = chainKey(p.chain);
        const row = rows.get(key);
        if (row) row.valueUsd -= p.amount_usd!;
        else unplaced += p.amount_usd!;
      }
    }
    if (unplaced >= 1) {
      notes.push(
        `Stablecoin reserve holdings of ${compactUsd(unplaced)} have no chain in the API, so they are counted in both their class and the stablecoin here, and the networks add up to a little more than the headline.`,
      );
    }
  }
  if (rows.has(UNSPLIT)) notes.push("Not split by chain: protocol TVL, which DefiLlama does not split, and stock tokens whose chain supplies include bridged copies.");
  return { rows: finish(rows, headline), headline, notes };
}

// ---- issuers and platforms ----

export function issuerLeague({ index, boards, venues, stablecoins }: LeagueInput): League {
  const headline = leagueHeadline(index, stablecoins !== null);
  const rows = new Map<string, LeagueRow & { sources: Set<string> }>();
  for (const board of boards) {
    const label = classInfo(board.asset_class)?.short ?? board.name;
    for (const issuer of board.issuers) {
      bump(rows, issuer.id, issuer.name, finite(issuer.value_usd) ? issuer.value_usd : 0, issuer.entries, label);
    }
  }
  // Stock tokens: their own issuer where the board names one (Robinhood's tokens are Robinhood's), else an
  // issuing venue's; a broker or exchange that only lists tokens issues none of them.
  let unnamed = 0;
  for (const venue of venues) {
    if (!venue.board) continue;
    for (const token of venue.board.tokens) {
      const issuer = token.issuer ?? (venue.board.venue.kind === "issuer" ? { id: venue.id, name: venue.board.venue.name } : null);
      if (!issuer) {
        unnamed += 1;
        continue;
      }
      bump(rows, issuer.id, issuer.name, finite(token.market_cap) ? token.market_cap : 0, 1, "Stocks");
    }
  }
  const notes: string[] = [];
  if (unnamed) notes.push(`${unnamed} stock ${unnamed === 1 ? "token has" : "tokens have"} no issuer in the API and ${unnamed === 1 ? "is" : "are"} left out.`);
  if (stablecoins) {
    const byIssuer = stablecoins.totals?.by_issuer;
    if (byIssuer?.length) {
      // Take off what the classes already count: a fund listed as a stablecoin too, and a reserve holding.
      const coinIssuer = new Map(stablecoins.stablecoins.map((c) => [c.id, c.issuer?.id ?? null]));
      const minus = new Map<string, { value: number; coins: number }>();
      for (const o of (index.overlap ?? []).filter((x) => x.deducted)) {
        const id = coinIssuer.get(o.stablecoin.id);
        if (!id) continue;
        const m = minus.get(id) ?? { value: 0, coins: 0 };
        m.value += o.value_usd ?? 0;
        if (o.reason !== "backed_by") m.coins += 1;
        minus.set(id, m);
      }
      for (const issuer of byIssuer) {
        if (!finite(issuer.market_cap)) continue;
        const m = minus.get(issuer.id);
        bump(rows, issuer.id, issuer.name, issuer.market_cap - (m?.value ?? 0), Math.max(0, issuer.stablecoins - (m?.coins ?? 0)), "Stablecoins");
      }
      const named = byIssuer.reduce((sum, i) => sum + (finite(i.market_cap) ? i.market_cap : 0), 0);
      const rest = (stablecoins.totals.market_cap ?? 0) - named;
      if (rest >= 1) notes.push(`Stablecoins worth ${compactUsd(rest)} have no issuer in the API and are left out.`);
    } else {
      notes.push("Stablecoin issuers are not in the API yet, so stablecoins are left out of this table.");
    }
  }
  return { rows: finish(rows, headline), headline, notes };
}

export function platformLeague({ index, boards, venues, stablecoins }: LeagueInput): League {
  const headline = leagueHeadline(index, stablecoins !== null);
  const rows = new Map<string, LeagueRow & { sources: Set<string> }>();
  let registryPlatforms = 0;
  for (const board of boards) {
    const label = classInfo(board.asset_class)?.short ?? board.name;
    for (const platform of board.platforms ?? []) {
      registryPlatforms += 1;
      bump(rows, platform.id, platform.name, finite(platform.value_usd) ? platform.value_usd : 0, platform.entries, label);
    }
  }
  for (const venue of venues) {
    if (!venue.board) continue;
    const cap = venue.board.totals.market_cap;
    bump(rows, venue.id, venue.board.venue.name, finite(cap) ? cap : 0, venue.board.tokens.length, venue.board.venue.kind === "issuer" ? "Stocks" : `Stocks (${venue.board.venue.kind})`);
    // The venue's own page, also when a registry platform of the same company made the row.
    rows.get(venue.id)!.href = `/tokenization?venue=${encodeURIComponent(venue.id)}&screen=dashboard`;
  }
  const notes: string[] = [];
  if (!registryPlatforms && boards.length) notes.push("The class boards do not list platforms yet, so only stock venues are shown.");
  if (stablecoins) notes.push("Stablecoins have no platform in the API (each coin is its own issuer's), so they are left out of this table.");
  return { rows: finish(rows, headline), headline, notes };
}

export function classLeague({ index, stablecoins }: LeagueInput): League {
  const includeStablecoins = stablecoins !== null;
  const headline = leagueHeadline(index, includeStablecoins);
  const overlap = index.overlap_value_usd ?? 0;
  const rows = new Map<string, LeagueRow & { sources: Set<string> }>();
  for (const item of index.classes) {
    if (item.id === "stablecoin" && !includeStablecoins) continue;
    const info = classInfo(item.id);
    const raw = item.totals.value_usd;
    if (!finite(raw)) continue;
    // The stablecoin copy of what the classes already count is the part the headline leaves out.
    const value = item.id === "stablecoin" ? raw - overlap : raw;
    bump(rows, item.id, info?.label ?? item.name, value, item.totals.entries ?? 0, "", {
      change7d: item.change_7d ?? item.totals.change_7d ?? null,
      flow7d: cleanFlow(item.net_flow_7d_usd ?? item.totals.net_flow_7d_usd),
      detail: item.id === "stablecoin" && overlap > 0 ? "net of what other classes already count" : null,
      href: info ? classHref(info) : null,
    });
  }
  const done = finish(rows, headline);
  return { rows: done.map((row) => (row.detail === "" ? { ...row, detail: null } : row)), headline, notes: [] };
}

export function league(tab: LeagueTab, input: LeagueInput): League {
  return tab === "networks" ? networkLeague(input) : tab === "issuers" ? issuerLeague(input) : tab === "platforms" ? platformLeague(input) : classLeague(input);
}

// ---- recently added ----

/** `GET /v1/classes/recent-additions` (C-7b): products added to tracking, newest first; starting sets apart. */
export interface RecentAdditions {
  schema: "panther.recent-additions.v1";
  as_of: string;
  total: number;
  launch: Array<{ class: string; peg: string | null; day: string; entries: number }>;
  items: Array<{
    class: string;
    entry_id: string;
    token_id: string | null;
    symbol: string;
    name: string;
    issuer: { id: string; name: string } | null;
    first_tracked: string;
    first_tracked_at: string | null;
    value_usd: number | null;
  }>;
}

export interface AddedItem {
  key: string;
  date: string;
  name: string;
  /** /v1/classes id. */
  classId: string;
  classLabel: string;
  valueUsd: number | null;
  /** The issuer, or for a grouped row what the group is. */
  detail: string | null;
  /** The class page, opening the token panel where the token has one. */
  href: string | null;
  /** A batch taken in at once ("+76 non-USD stablecoins"), shown as one line. */
  group?: boolean;
}

/**
 * The feed: the API's additions as rows, plus a later batch of a class's
 * launch (a class whose second group of products came in on one day, like the
 * non-USD stablecoins) as one grouped line. A class's first launch day is its
 * starting set and is not news.
 */
export function recentlyAdded(data: Pick<RecentAdditions, "items" | "launch">, limit = 50): AddedItem[] {
  const items: AddedItem[] = data.items.map((item) => {
    const info = classInfo(item.class);
    const base = info?.path ?? null;
    const panel = item.token_id !== null && !item.token_id.startsWith("offchain:");
    return {
      key: `${item.class}:${item.entry_id}`,
      date: item.first_tracked,
      name: item.symbol || item.name,
      classId: item.class,
      classLabel: info?.label ?? item.class,
      valueUsd: item.value_usd,
      detail: item.issuer?.name ?? null,
      href: base && panel ? `${base}?token=${encodeURIComponent(item.token_id!)}` : base,
    };
  });
  const firstDay = new Map<string, string>();
  for (const l of data.launch) if (!firstDay.has(l.class) || l.day < firstDay.get(l.class)!) firstDay.set(l.class, l.day);
  for (const l of data.launch) {
    if (l.day === firstDay.get(l.class)) continue;
    const info = classInfo(l.class);
    const what = l.class === "stablecoin" && l.peg === "other" ? "non-USD stablecoins" : `${info?.label ?? l.class} products`;
    items.push({
      key: `launch:${l.class}:${l.peg ?? ""}:${l.day}`,
      date: l.day,
      name: `+${l.entries} ${what}`,
      classId: l.class,
      classLabel: info?.label ?? l.class,
      valueUsd: null,
      detail: "added to tracking together",
      href: info?.path ?? null,
      group: true,
    });
  }
  return items.sort((a, b) => b.date.localeCompare(a.date) || Number(Boolean(b.group)) - Number(Boolean(a.group)) || (b.valueUsd ?? -1) - (a.valueUsd ?? -1)).slice(0, limit);
}
